/**
 * OrderFlow Page
 * Multi-step order form (guest/authenticated)
 * Steps: 1) Service Selection, 2) Schedule, 3) Location, 4) Details
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { OrderStepper } from '../../components/order/OrderStepper';
import { useProviderConfig } from '../../contexts/ProviderConfigContext';
import { checkoutService } from '../../services/checkout.service';
import { providerService } from '../../services/provider.service';
import { adaptServicesToWidget } from '../../services/widget-adapter.service';
import type { OrderForm } from '../../schemas/order.schema';
import { orderSchema } from '../../schemas/order.schema';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { FormSectionSkeleton } from '../../components/widget/FormFieldSkeleton';
import { Skeleton } from '../../components/common/Skeleton';
import { useRetry } from '../../hooks/useRetry';
import { getErrorMessage } from '../../utils/errorMessages';
import { ErrorAlert } from '../../components/widget/ErrorAlert';
import { Button } from '../../components/ui/button';
import { usePricingEstimation } from '../../hooks/usePricingEstimation';
import { buildPricingRequest } from '../../services/pricing-request-builder.service';

// Phase 1 step components (new delivery flow)
import { CustomerIntentStep } from './steps/CustomerIntentStep';
import { ScheduleFirstStep } from './steps/ScheduleFirstStep';
import { ContainerSelectionStep } from './steps/ContainerSelectionStep';
import { LocationStep } from './steps/LocationStep';
import { ServicesStep } from './steps/ServicesStep';
import { DetailsStep } from './steps/DetailsStep';

// Phase 2 step components (existing customer flow)
import { ContainerLookupStep } from './steps/ContainerLookupStep';
import { SwapOrPickupStep } from './steps/SwapOrPickupStep';
import { QuickScheduleStep } from './steps/QuickScheduleStep';

// Dynamic step configuration based on customer intent
const PHASE1_STEPS = ['Intent', 'Location', 'Container', 'Schedule', 'Services', 'Details'];
const PHASE2_STEPS = ['Intent', 'Lookup', 'Action', 'Schedule', 'Services', 'Details'];

/**
 * OrderFlowContent - Internal component that contains the actual order flow logic
 */
function OrderFlowContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const { config, isLoading: isConfigLoading } = useProviderConfig();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [serviceAreaError, setServiceAreaError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pricingUnavailable, setPricingUnavailable] = useState(false);
  const { retry: retryValidation, isRetrying: isRetryingValidation} = useRetry();
  const [pricingInfo, setPricingInfo] = useState<{
    type: 'fixed' | 'dynamic';
    fixedRates?: {
      '10Y': number;
      '20Y': number;
      '30Y': number;
      '40Y': number;
    };
    dynamicQuote?: {
      estimatedPriceCents: number;
      breakdown: {
        baseCostCents: number;
        distanceCostCents: number;
        timeCostCents: number;
        markupPercent: number;
      };
      estimatedDriveMinutes: number;
      estimatedDistanceMiles: number;
    };
  } | null>(null);

  // Initialize form with react-hook-form
  const methods = useForm<OrderForm>({
    resolver: zodResolver(orderSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      customerIntent: undefined,
      orderType: 'delivery',
      containerSize: location.state?.containerSize || '',
      materialType: '',
      deliveryDate: '',
      deliveryNotes: '',
      street: '',
      city: '',
      state: 'CA',
      zipCode: '',
      country: 'US',
      placementInstructions: '',
      existingOrderId: undefined,
    },
  });

  const { handleSubmit, trigger, watch, setFocus } = methods;

  // Refs for focus management
  const stepContentRef = useRef<HTMLDivElement>(null);

  // Watch customer intent to determine flow
  const customerIntent = watch('customerIntent');
  const isExistingCustomerFlow = customerIntent === 'existing_container';

  // Get current step labels and total
  const STEPS = isExistingCustomerFlow ? PHASE2_STEPS : PHASE1_STEPS;
  const TOTAL_STEPS = STEPS.length;

  // Adapt provider services to widget format for injection
  const availableServices = useMemo(() => {
    return adaptServicesToWidget(config?.additionalServices);
  }, [config?.additionalServices]);

  // Load saved cart from session storage on mount
  useEffect(() => {
    const savedCart = checkoutService.loadCartFromStorage();
    if (savedCart) {
      methods.reset({
        orderType: savedCart.orderType,
        containerSize: savedCart.containerSize,
        materialType: savedCart.materialType,
        deliveryDate: savedCart.deliveryDate,
        street: savedCart.serviceAddress.street,
        city: savedCart.serviceAddress.city,
        state: savedCart.serviceAddress.state,
        zipCode: savedCart.serviceAddress.zipCode,
        country: savedCart.serviceAddress.country || 'US',
        placementInstructions: savedCart.placementInstructions || '',
      });
    }
  }, [methods]);

  // Save cart to session storage on form changes
  const formData = watch();
  useEffect(() => {
    // Only save if we have some data
    if (formData.containerSize && formData.orderType && formData.materialType) {
      checkoutService.saveCartToStorage({
        orderType: formData.orderType as 'delivery' | 'swap' | 'pickup',
        containerSize: formData.containerSize as '10Y' | '20Y' | '30Y' | '40Y',
        materialType: formData.materialType,
        deliveryDate: formData.deliveryDate || '',
        serviceAddress: {
          street: formData.street || '',
          city: formData.city || '',
          state: formData.state || 'CA',
          zipCode: formData.zipCode || '',
          country: formData.country || 'US',
        },
        placementInstructions: formData.placementInstructions || '',
      });
    }
  }, [formData]);

  // Get pricing data for acknowledgment validation (only on Details step)
  const pricingParams = useMemo(() => {
    if (currentStep !== TOTAL_STEPS || !formData.containerSize || !formData.materialType) {
      return null;
    }
    // Type assertion to access PRRO fields (safe because they're optional)
    const formWithPrro = formData as Partial<{
      estimatedTonnage?: number;
      hasTreatedWood?: boolean;
      estimatedWeightTons?: number;
    }>;
    return buildPricingRequest({
      containerSize: formData.containerSize,
      materialType: formData.materialType,
      selectedServices: formData.selectedServices,
      estimatedTonnage: formWithPrro.estimatedTonnage,
      hasTreatedWood: formWithPrro.hasTreatedWood,
      estimatedWeightTons: formWithPrro.estimatedWeightTons,
    });
  }, [currentStep, TOTAL_STEPS, formData]);

  const { pricing } = usePricingEstimation(
    providerSlug || '',
    pricingParams
  );

  // Check if all required acknowledgments are acknowledged
  const allAcknowledgmentsMet = useMemo(() => {
    if (currentStep !== TOTAL_STEPS) return true; // Only validate on final step
    const required = pricing?.requiredAcknowledgments || [];
    if (required.length === 0) return true;
    // Type assertion to access acknowledgedTerms (safe because it's optional)
    const formWithAck = formData as Partial<{ acknowledgedTerms?: string[] }>;
    const acknowledgedTerms = formWithAck.acknowledgedTerms || [];
    return required.every(ack => acknowledgedTerms.includes(ack.id));
  }, [currentStep, TOTAL_STEPS, pricing?.requiredAcknowledgments, formData]);

  /**
   * Validate service area (extracted for retry logic)
   */
  const validateServiceArea = async (address: { street: string; city: string; state: string; zipCode: string }) => {
    if (!providerSlug) {
      throw new Error('Provider not found');
    }

    setIsValidatingAddress(true);
    try {
      const result = await providerService.validateServiceArea(providerSlug, address);

      if (!result.inServiceArea) {
        setServiceAreaError(result.message || 'Not in service area');
        setPricingInfo(null);
        return false;
      }

      // Handle pricing unavailable state
      if (result.pricingUnavailable) {
        setPricingUnavailable(true);
        setPricingInfo(null);
      } else if (result.pricing) {
        setPricingInfo(result.pricing);
        setPricingUnavailable(false);
      }

      // Log jurisdiction info if available (for debugging)
      if (result.jurisdiction) {
        console.log('Validated jurisdiction:', result.jurisdiction);
      }

      return true;
    } finally {
      setIsValidatingAddress(false);
    }
  };

  /**
   * Validate current step and proceed to next
   */
  const handleNext = async () => {
    // Clear any previous errors
    setServiceAreaError(null);
    setPricingUnavailable(false);

    let fieldsToValidate: (keyof OrderForm)[] = [];

    if (isExistingCustomerFlow) {
      // Phase 2: Existing customer flow validation
      switch (currentStep) {
        case 1:
          // Customer Intent step
          fieldsToValidate = [];
          break;
        case 2:
          // Container Lookup step
          fieldsToValidate = ['existingOrderId'];
          break;
        case 3:
          // Swap or Pickup step
          fieldsToValidate = ['orderType'];
          break;
        case 4:
          // Quick Schedule step
          fieldsToValidate = ['deliveryDate'];
          break;
        case 5:
          // Services step
          fieldsToValidate = [];
          break;
        case 6:
          // Details step
          fieldsToValidate = [];
          break;
      }
    } else {
      // Phase 1: New delivery flow validation
      switch (currentStep) {
        case 1:
          // Customer Intent step
          fieldsToValidate = [];
          break;
        case 2:
          // Location step
          fieldsToValidate = ['street', 'city', 'state', 'zipCode'];
          break;
        case 3:
          // Container Selection step
          fieldsToValidate = ['containerSize', 'materialType'];
          break;
        case 4:
          // Schedule First step
          fieldsToValidate = ['deliveryDate'];
          break;
        case 5:
          // Services step
          fieldsToValidate = [];
          break;
        case 6:
          // Details step
          fieldsToValidate = [];
          break;
      }
    }

    // Validate fields
    const isValid = await trigger(fieldsToValidate);

    if (!isValid) {
      // Focus on first error field
      const errors = methods.formState.errors;
      const firstErrorField = fieldsToValidate.find(field => errors[field]);
      if (firstErrorField) {
        setTimeout(() => {
          setFocus(firstErrorField);
        }, 100);
      }
      return;
    }

    if (isValid) {
      // Additional validation for Location step (step 2 in new delivery flow)
      if (!isExistingCustomerFlow && currentStep === 2) {
        const { street = '', city = '', state = 'CA', zipCode = '' } = formData;

        // Validate service area via API with full address for geospatial validation
        if (zipCode && providerSlug) {
          try {
            const isValidArea = await validateServiceArea({ street, city, state, zipCode });
            if (!isValidArea) {
              return; // Prevent navigation if not in service area
            }
          } catch (error) {
            console.error('Service area validation error:', error);
            const errorMessage = getErrorMessage(error);
            setServiceAreaError(errorMessage.message);
            return;
          }
        }
      }

      if (currentStep < TOTAL_STEPS) {
        setCurrentStep((prev) => prev + 1);
        // Scroll to top of step content and announce step change
        setTimeout(() => {
          stepContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          stepContentRef.current?.focus();
        }, 100);
      }
    }
  };

  /**
   * Go back to previous step
   */
  const handleBack = () => {
    // Clear service area error, pricing unavailable, and pricing when navigating back from location step
    setServiceAreaError(null);
    setPricingUnavailable(false);
    if (currentStep === 3 && !isExistingCustomerFlow) {
      // User is going back from Container Selection to Location
      setPricingInfo(null);
    }

    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      // Scroll to top of step content
      setTimeout(() => {
        stepContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        stepContentRef.current?.focus();
      }, 100);
    }
  };

  /**
   * Submit final order form
   */
  const onSubmit = async () => {
    // Validate acknowledgments before proceeding
    if (!allAcknowledgmentsMet) {
      const errorMsg = 'Please acknowledge all required items before proceeding to checkout.';
      setSubmitError(errorMsg);
      // Scroll to acknowledgments section
      setTimeout(() => {
        stepContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Navigate to checkout with cart data
      navigate('../checkout');
    } catch (error) {
      console.error('Error submitting order:', error);
      const errorMessage = getErrorMessage(error);
      setSubmitError(errorMessage.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading skeleton while config is loading
  if (isConfigLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header skeleton */}
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-8 w-48" />
              </div>
              <Skeleton className="h-5 w-24" />
            </div>
          </div>
        </header>

        {/* Main content skeleton */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stepper skeleton */}
          <div className="mb-8 flex items-center justify-between">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <Skeleton className="h-10 w-10 rounded-full" />
                {step < 5 && <Skeleton className="h-1 w-16 mx-2" />}
              </div>
            ))}
          </div>

          {/* Form skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <FormSectionSkeleton fieldCount={4} />
          </div>

          {/* Navigation buttons skeleton */}
          <div className="flex items-center justify-between mt-6">
            <Skeleton className="h-12 w-24 rounded-md" />
            <Skeleton className="h-12 w-32 rounded-md" />
          </div>
        </main>
      </div>
    );
  }

  // Render current step component
  const renderStep = () => {
    if (isExistingCustomerFlow) {
      // Phase 2: Existing customer flow
      switch (currentStep) {
        case 1:
          return <CustomerIntentStep onIntentSelected={handleNext} />;
        case 2:
          return <ContainerLookupStep />;
        case 3:
          return <SwapOrPickupStep />;
        case 4:
          return <QuickScheduleStep />;
        case 5:
          return <ServicesStep availableServices={availableServices} />;
        case 6:
          return <DetailsStep />;
        default:
          return null;
      }
    } else {
      // Phase 1: New delivery flow
      switch (currentStep) {
        case 1:
          return <CustomerIntentStep onIntentSelected={handleNext} />;
        case 2:
          return (
            <LocationStep
              pricingInfo={pricingInfo}
              isValidating={isValidatingAddress}
              pricingUnavailable={pricingUnavailable}
            />
          );
        case 3:
          return <ContainerSelectionStep />;
        case 4:
          return <ScheduleFirstStep />;
        case 5:
          return <ServicesStep availableServices={availableServices} />;
        case 6:
          return <DetailsStep />;
        default:
          return null;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip Navigation Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:border-2 focus:border-primary focus:rounded focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Header - Matching ServiceCatalog styling */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {config?.logoUrl && (
                <img
                  src={config.logoUrl}
                  alt={`${config.companyName} logo`}
                  className="h-10 w-auto"
                />
              )}
              <h1 className="text-2xl font-bold text-gray-900">
                {config?.companyName || 'Order Service'}
              </h1>
            </div>
            <div className="text-sm text-gray-600">
              Step {currentStep} of {TOTAL_STEPS}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main id="main-content" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stepper */}
        <OrderStepper currentStep={currentStep} totalSteps={TOTAL_STEPS} stepLabels={STEPS} />

        {/* Form */}
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
            onKeyDown={(e) => {
              // Allow Enter key to submit current step (except in textarea)
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
                if (currentStep < TOTAL_STEPS) {
                  handleNext();
                } else {
                  handleSubmit(onSubmit)();
                }
              }
            }}
          >
            {/* Current step content */}
            <div
              ref={stepContentRef}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              role="region"
              aria-label={`Step ${currentStep} of ${TOTAL_STEPS}: ${STEPS[currentStep - 1]}`}
              tabIndex={-1}
            >
              {renderStep()}
            </div>

            {/* Service Area Error Message */}
            {serviceAreaError && (
              <ErrorAlert
                error={{ message: serviceAreaError, suggestion: 'Please check the address or contact support.' }}
                onRetry={() => {
                  const { street = '', city = '', state = 'CA', zipCode = '' } = formData;
                  retryValidation(() => validateServiceArea({ street, city, state, zipCode }))
                    .then((isValid) => {
                      if (isValid) {
                        setServiceAreaError(null);
                      }
                    })
                    .catch((err) => {
                      const errorMessage = getErrorMessage(err);
                      setServiceAreaError(errorMessage.message);
                    });
                }}
                isRetrying={isRetryingValidation}
                dismissible
                onDismiss={() => setServiceAreaError(null)}
              />
            )}

            {/* Submit Error Message */}
            {submitError && (
              <ErrorAlert
                error={{ message: submitError }}
                dismissible
                onDismiss={() => setSubmitError(null)}
              />
            )}

            {/* Navigation buttons - Hidden on step 1 (intent selection auto-advances) */}
            {currentStep > 1 && (
              <div className="flex items-center justify-between" role="navigation" aria-label="Form navigation">
                <button
                  type="button"
                  onClick={handleBack}
                  className="min-h-[44px] px-6 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  aria-label={`Go back to step ${currentStep - 1}: ${STEPS[currentStep - 2]}`}
                >
                  Back
                </button>

                {currentStep < TOTAL_STEPS ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="min-h-[44px] px-6 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                    aria-label={`Continue to step ${currentStep + 1}: ${STEPS[currentStep]}`}
                  >
                    Continue
                  </button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isSubmitting || !allAcknowledgmentsMet}
                    className="min-h-[44px] px-6 py-3"
                    aria-label="Proceed to checkout"
                  >
                    {isSubmitting ? 'Processing...' : 'Proceed to Checkout'}
                  </Button>
                )}
              </div>
            )}
          </form>
        </FormProvider>
      </main>
    </div>
  );
}

/**
 * OrderFlow - Main export with error boundary
 */
export function OrderFlow() {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log error to console in development
        console.error('OrderFlow Error:', error, errorInfo);

        // In production, you would send this to your error tracking service
        // e.g., Sentry, LogRocket, etc.
      }}
    >
      <OrderFlowContent />
    </ErrorBoundary>
  );
}
